import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import dreamWorldProvider from "../DreamWorldProvider";
import { Event } from "../types/events";
import { dummySubmissions, Submission } from "../utils/dummySubmissions";
import SubmissionCard from "../components/events/SubmissionCard";
import PreviewModal from "../components/library/PreviewModal"; // Re-using existing component
import LoadingIndicator from "../components/ui/LoadingIndicator";

const EventSubmissionsPage: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const { id } = useParams<{ id: string }>();
  const [event, setEvent] = useState<Event | null>(null);
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [filter, setFilter] = useState("Most Recent");
  const [selectedSubmission, setSelectedSubmission] =
    useState<Submission | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;

    const fetchEvent = async () => {
      try {
        const eventData = await dreamWorldProvider.getEvent(id);
        setEvent(eventData);
        if (eventData) {
          // TODO: Fetch real submissions
          const eventSubmissions = dummySubmissions.filter(
            (s) => s.eventId === id
          );
          setSubmissions(eventSubmissions);
        }
      } catch (error) {
        console.error("Error fetching event: ", error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [id]);

  const handleOpenModal = (submission: Submission) => {
    const content = {
      id: submission.id,
      thumbnailUrl: submission.imageUrl,
      prompt: submission.prompt,
      type: "image",
    };
    //I will just leave this thing for now as I do not have all the data for the user
    // setSelectedSubmission(content)
  };

  const sortedSubmissions = [...submissions].sort((a, b) => {
    if (filter === "Most Popular") return b.likes - a.likes;
    if (filter === "Top Rated") return b.views - a.views;
    return 0; // 'Most Recent' is the default order from dummy data
  });

  if (loading) {
    return (
      <div className="text-center text-white py-10">
        <LoadingIndicator />
      </div>
    );
  }

  if (!event) {
    return <div className="text-center text-white py-10">Event not found.</div>;
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white p-4 md:p-8">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-4xl font-bold text-center mb-2">
          Submissions for "{event.title}"
        </h1>
        <p className="text-lg text-gray-400 text-center mb-8">
          Browse, vote, and get inspired by the community's creations.
        </p>
      </motion.div>

      {/* Filters */}
      <div className="flex justify-center space-x-4 mb-8">
        {["Most Recent", "Most Popular", "Top Rated"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-full font-semibold transition-colors ${
              filter === f
                ? "bg-indigo-600 text-white"
                : "bg-gray-800 hover:bg-gray-700"
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Submissions Grid */}
      <div
        className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4"
        style={{ gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))" }}
      >
        {sortedSubmissions.map((submission) => (
          <SubmissionCard
            key={submission.id}
            submission={submission}
            onView={() => handleOpenModal(submission)}
          />
        ))}
      </div>

      {/* Modal */}
      {/* {selectedSubmission && (
        <PreviewModal
          content={selectedSubmission}
          onClose={() => setSelectedSubmission(null)}
        />
      )} */}
    </div>
  );
};

export default EventSubmissionsPage;
